import React, { useCallback } from 'react'
import { Button } from '@mantine/core'
import { RiskReviewSection } from '../../../../shared/interfaces/project/risk/risk-inteface'

interface RiskReviewSectionNavigationProps {
  category: string
  mandatoryRiskCategoriesDisplayNamesToKeys: Record<string, string>
  sectionIndexes: Record<string, number>
  reviewSectionHolders: Array<{
    key: string
    sectionPages?: Array<RiskReviewSection[]>
  }>
  setSectionForNavigation: (sectionKey: string | null) => void
  navigateSection: (sectionKey: string, forward: boolean) => void
  userFeatureFlags?: {
    risk_review_section?: boolean
  }
  reviewSectionQueryLoading?: boolean
}

const RiskReviewSectionNavigation: React.FC<
  RiskReviewSectionNavigationProps
> = ({
  category,
  mandatoryRiskCategoriesDisplayNamesToKeys,
  sectionIndexes,
  reviewSectionHolders,
  setSectionForNavigation,
  navigateSection,
  userFeatureFlags,
  reviewSectionQueryLoading,
}) => {
  const categoryKey = mandatoryRiskCategoriesDisplayNamesToKeys[category]

  const reviewNotStarted = (category: string) => {
    return (
      mandatoryRiskCategoriesDisplayNamesToKeys[category] &&
      sectionIndexes[mandatoryRiskCategoriesDisplayNamesToKeys[category]] === -1
    )
  }

  const canDisplaySectionControls = useCallback(
    (category: string) => {
      const sectionHolder = reviewSectionHolders.find(
        (r) => r.key === mandatoryRiskCategoriesDisplayNamesToKeys[category]
      )
      //make sure we are on the right document. wait for the results to load
      if (
        !sectionHolder ||
        !sectionHolder.sectionPages ||
        sectionHolder.sectionPages.length === 0
      ) {
        return false
      }
      //find any content segments from sectionHolder
      const contentSegments = sectionHolder.sectionPages
        .flat()
        .filter((section) => section.content_segment)
        .map((section) => section.content_segment)
      if (contentSegments.length === 0) {
        return false
      }
      return (
        userFeatureFlags?.risk_review_section &&
        mandatoryRiskCategoriesDisplayNamesToKeys[category]
      )
    },
    [
      reviewSectionHolders,
      userFeatureFlags?.risk_review_section,
      mandatoryRiskCategoriesDisplayNamesToKeys,
    ]
  )

  const canDisplaySectionNavigation = (category: string) => {
    return (
      !reviewSectionQueryLoading &&
      canDisplaySectionControls(category) &&
      sectionIndexes[mandatoryRiskCategoriesDisplayNamesToKeys[category]] !== -1
    )
  }

  const canDisplayStartReview = useCallback(
    (category: string) => {
      return (
        !reviewSectionQueryLoading &&
        canDisplaySectionControls(category) &&
        reviewNotStarted(category)
      )
    },
    [reviewSectionQueryLoading, canDisplaySectionControls, reviewNotStarted]
  )

  const shouldDisplayNextForSection = useCallback(
    (sectionKey: string) => {
      const sectionHolder = reviewSectionHolders.find(
        (r) => r.key === sectionKey
      )
      const sectionIndex = sectionIndexes[sectionKey]
      if (
        !sectionHolder ||
        !sectionHolder.sectionPages ||
        sectionHolder.sectionPages.length === 0 ||
        sectionIndex === undefined
      ) {
        return false
      }
      const totalPages = sectionHolder.sectionPages.length
      return totalPages > 0 && sectionIndex < totalPages - 1
    },
    [sectionIndexes, reviewSectionHolders]
  )

  const shouldDisplayPrevForSection = useCallback(
    (sectionKey: string) => {
      const sectionHolder = reviewSectionHolders.find(
        (r) => r.key === sectionKey
      )
      const sectionIndex = sectionIndexes[sectionKey]
      if (
        !sectionHolder ||
        !sectionHolder.sectionPages ||
        sectionHolder.sectionPages.length == 0 ||
        sectionIndex === undefined
      )
        return false
      const totalPages = sectionHolder.sectionPages.length
      return totalPages > 0 && sectionIndex > 0
    },
    [sectionIndexes, reviewSectionHolders]
  )

  const navigateBack = useCallback(() => {
    setSectionForNavigation(categoryKey)
    navigateSection(categoryKey, false)
  }, [categoryKey, setSectionForNavigation, navigateSection])

  const navigateForward = useCallback(() => {
    setSectionForNavigation(categoryKey)
    navigateSection(categoryKey, true)
  }, [categoryKey, setSectionForNavigation, navigateSection])

  const showLoadingButton = useCallback(
    () =>
      mandatoryRiskCategoriesDisplayNamesToKeys[category] &&
      reviewSectionQueryLoading &&
      !canDisplayStartReview(category),
    [
      mandatoryRiskCategoriesDisplayNamesToKeys,
      category,
      reviewSectionQueryLoading,
      canDisplayStartReview,
    ]
  )

  return (
    <div className="ml-2 flex-shrink-0">
      {showLoadingButton() && <Button variant="subtle">Loading...</Button>}
      {canDisplaySectionNavigation(category) && (
        <div className="flex flex-row items-center">
          <Button
            variant="subtle"
            disabled={!shouldDisplayPrevForSection(categoryKey)}
            onClick={navigateBack}
          >
            Prev
          </Button>
          <div>
            {sectionIndexes[categoryKey] + 1}/
            {reviewSectionHolders.find((r) => r.key === categoryKey)
              ?.sectionPages?.length || 1}
          </div>
          <Button
            variant="subtle"
            disabled={!shouldDisplayNextForSection(categoryKey)}
            onClick={navigateForward}
          >
            Next
          </Button>
        </div>
      )}

      {canDisplayStartReview(category) && (
        <div className="flex flex-row items-center">
          <Button variant="subtle" onClick={navigateForward}>
            Start Review
          </Button>
        </div>
      )}
    </div>
  )
}

export default RiskReviewSectionNavigation
